import React, { useEffect, useState } from 'react'
import moment from 'moment'
import validator from 'validator'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import AddLinkIcon from '@mui/icons-material/AddLink'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import SendIcon from '@mui/icons-material/Send'
import API from '../../../../services/API'
import { codeFont } from './ShareCodeCard'
import {
  EXPIRY_OPTIONS,
  generateShareCode,
  getAccessRecordUrl,
} from './shareCodeUtils'

const defaultHours =
  EXPIRY_OPTIONS.find((option) => option.isDefault)?.hours ?? 12

const Label = ({ children }) => (
  <Typography
    variant="caption"
    color="text.secondary"
    sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}
  >
    {children}
  </Typography>
)

const CreateShareCodeDialog = ({ open, onClose, ePCRID, onCreated, onCopy }) => {
  const [hours, setHours] = useState(defaultHours)
  const [email, setEmail] = useState('')
  const [emailError, setEmailError] = useState(null)
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState(null)
  const [created, setCreated] = useState(null)
  // 'idle' | 'sending' | 'sent' | 'failed'
  const [emailStatus, setEmailStatus] = useState('idle')

  useEffect(() => {
    if (!open) return
    setHours(defaultHours)
    setEmail('')
    setEmailError(null)
    setCreating(false)
    setError(null)
    setCreated(null)
    setEmailStatus('idle')
  }, [open])

  const accessUrl = getAccessRecordUrl()

  const sendEmail = async (code) => {
    const trimmed = email.trim()
    if (!validator.isEmail(trimmed)) {
      setEmailError('Please enter a valid email address.')
      return
    }
    setEmailError(null)
    setEmailStatus('sending')
    try {
      const res = await API.post('/epcr/email-share-code', {
        email: trimmed,
        code,
      })
      setEmailStatus(res.status === 200 ? 'sent' : 'failed')
    } catch (e) {
      setEmailStatus('failed')
    }
  }

  const handleCreate = async () => {
    const trimmed = email.trim()
    if (trimmed && !validator.isEmail(trimmed)) {
      setEmailError('Please enter a valid email address.')
      return
    }
    setEmailError(null)
    setCreating(true)
    setError(null)
    try {
      const code = generateShareCode()
      const res = await API.post('/epcr/create-share-code', {
        ePCRID,
        code,
        expires: moment().add(hours, 'hours').toDate(),
      })
      if (res.status === 200) {
        setCreated(res.data)
        onCreated?.(res.data)
        if (trimmed) sendEmail(res.data.code)
      } else if (res.status === 403) {
        setError(
          'Only the current owner of this record can create share codes, and not while it is held by a treatment location.',
        )
      } else if (res.status === 400) {
        setError(res.data?.error || 'That share code was not accepted.')
      } else {
        setError('Unable to create a share code right now. Please try again.')
      }
    } catch (e) {
      setError('Connection lost. The share code was not created.')
    } finally {
      setCreating(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          {created ? (
            <CheckCircleIcon color="success" />
          ) : (
            <AddLinkIcon color="anMainBlue" />
          )}
          <span>{created ? 'Share code ready' : 'New share code'}</span>
        </Stack>
      </DialogTitle>

      <DialogContent>
        {!created ? (
          <Stack spacing={2.5} mt={0.5}>
            <Typography color="text.secondary">
              A share code lets someone without an AmbuNet account download
              this record once they confirm the patient&apos;s surname and
              date of birth. Every attempt is logged against the code.
            </Typography>

            <Stack spacing={1}>
              <Label>How long should it work for?</Label>
              <Stack direction="row" gap={1} flexWrap="wrap">
                {EXPIRY_OPTIONS.map((option) => (
                  <Chip
                    key={option.hours}
                    label={option.label}
                    clickable
                    color={hours === option.hours ? 'anMainBlue' : 'default'}
                    variant={hours === option.hours ? 'filled' : 'outlined'}
                    onClick={() => setHours(option.hours)}
                    sx={{ fontWeight: 600, borderRadius: 1 }}
                  />
                ))}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {EXPIRY_OPTIONS.find((option) => option.hours === hours)
                  ?.caption ?? ''}
                {' · '}Works until{' '}
                {moment().add(hours, 'hours').format('HH:mm on ddd D MMM')}.
                Codes can be revoked at any time.
              </Typography>
            </Stack>

            <Stack spacing={1}>
              <Label>Send it by email (optional)</Label>
              <TextField
                size="small"
                fullWidth
                type="email"
                placeholder="recipient@hospital.nhs.uk"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                error={!!emailError}
                helperText={
                  emailError ??
                  'The recipient gets the code and a link to the access page.'
                }
                autoComplete="off"
              />
            </Stack>

            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        ) : (
          <Stack spacing={2} mt={0.5}>
            <Box
              sx={{
                p: 2.5,
                borderRadius: 2.5,
                textAlign: 'center',
                bgcolor: 'rgba(0, 150, 76, 0.08)',
                border: '1px solid rgba(0, 150, 76, 0.3)',
              }}
            >
              <Typography
                sx={{ ...codeFont, fontSize: '2.2rem', color: 'anBlue.main' }}
              >
                {created.code}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Works until{' '}
                {moment(created.expires).format('HH:mm on ddd D MMM')} (
                {moment(created.expires).fromNow()})
              </Typography>
              <Button
                size="small"
                startIcon={<ContentCopyIcon />}
                onClick={() => onCopy?.(created.code, 'Share code')}
                sx={{ mt: 1 }}
              >
                Copy code
              </Button>
            </Box>

            <Stack spacing={0.5}>
              <Label>Access page</Label>
              <Stack direction="row" gap={0.5} alignItems="center">
                <Typography
                  variant="body2"
                  sx={{ overflowWrap: 'anywhere', flex: 1 }}
                >
                  {accessUrl}
                </Typography>
                <IconButton
                  size="small"
                  aria-label="Copy access link"
                  onClick={() => onCopy?.(accessUrl, 'Access link')}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                The recipient enters the code with the patient&apos;s surname
                and date of birth, then downloads the record as a PDF.
              </Typography>
            </Stack>

            {emailStatus === 'sent' ? (
              <Alert severity="success">Instructions emailed to {email.trim()}.</Alert>
            ) : (
              <Stack spacing={1}>
                <Label>
                  {emailStatus === 'failed'
                    ? 'Email not sent'
                    : 'Send it by email'}
                </Label>
                <Stack direction="row" gap={1} alignItems="flex-start">
                  <TextField
                    size="small"
                    fullWidth
                    type="email"
                    placeholder="recipient@hospital.nhs.uk"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    error={!!emailError || emailStatus === 'failed'}
                    helperText={
                      emailError ??
                      (emailStatus === 'failed'
                        ? 'The email could not be sent. Check the address and try again.'
                        : ' ')
                    }
                    autoComplete="off"
                  />
                  <Button
                    variant="outlined"
                    startIcon={<SendIcon />}
                    disabled={emailStatus === 'sending' || !email.trim()}
                    onClick={() => sendEmail(created.code)}
                    sx={{ flexShrink: 0 }}
                  >
                    {emailStatus === 'sending' ? 'Sending…' : 'Send'}
                  </Button>
                </Stack>
              </Stack>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {!created ? (
          <>
            <Button onClick={onClose} color="inherit">
              Cancel
            </Button>
            <Button
              variant="contained"
              color="anMainBlue"
              startIcon={<AddLinkIcon />}
              disabled={creating || !ePCRID}
              onClick={handleCreate}
            >
              {creating ? 'Creating…' : 'Generate share code'}
            </Button>
          </>
        ) : (
          <Button variant="contained" onClick={onClose}>
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default CreateShareCodeDialog
